import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";

const Product = () => {
  const [products, setProducts] = useState([]);
  const [cartItems, setCartItems] = useState([]);
  const [addedToCart, setAddedToCart] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch("https://servercareseebd.careseebd.com/api/v1/products");
        const data = await response.json();
        setProducts(data || []);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    const cart = JSON.parse(localStorage.getItem("cart")) || [];
    setCartItems(cart);
    const cartMap = {};
    cart.forEach((item) => {
      cartMap[item._id] = item.quantity;
    });
    setAddedToCart(cartMap);
  }, []);

  const addToCart = (product) => {
    let cart = JSON.parse(localStorage.getItem("cart")) || [];
    const existingItemIndex = cart.findIndex(
      (item) => item._id === product._id
    );

    if (existingItemIndex !== -1) {
      cart[existingItemIndex].quantity += 1;
    } else {
      cart.push({ ...product, quantity: 1 });
    }
    localStorage.setItem("cart", JSON.stringify(cart));
    setCartItems(cart);
    setAddedToCart((prev) => ({
      ...prev,
      [product._id]: cart[existingItemIndex]?.quantity || 1,
    }));
  };

  const removeFromCart = (productId) => {
    let cart = JSON.parse(localStorage.getItem("cart")) || [];
    const existingItemIndex = cart.findIndex((item) => item._id === productId);

    if (existingItemIndex !== -1) {
      if (cart[existingItemIndex].quantity > 1) {
        cart[existingItemIndex].quantity -= 1;
        localStorage.setItem("cart", JSON.stringify(cart));
        setCartItems(cart);
        setAddedToCart((prev) => ({
          ...prev,
          [productId]: cart[existingItemIndex].quantity,
        }));
      } else {
        cart = cart.filter((item) => item._id !== productId);
        localStorage.setItem("cart", JSON.stringify(cart));
        setCartItems(cart);
        setAddedToCart((prev) => {
          const updated = { ...prev };
          delete updated[productId];
          return updated;
        });
      }
    }
  };

  const categories = ["All", ...new Set(products.map((p) => p.categories || "Accessories").filter(Boolean))];

  const filteredProducts = selectedCategory === "All"
    ? products
    : products.filter((p) => (p.categories || "Accessories") === selectedCategory);

  const totalCartQty = cartItems.reduce((total, item) => total + item.quantity, 0);

  return (
    <div className="bg-slate-50 min-h-screen pb-32 relative">
      {/* Decorative top soft background gradient */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_0%,rgba(99,102,241,0.06),transparent)] pointer-events-none" />

      {/* Floating Cart Icon, positioned in the top-right corner of the view */}
      <div className="fixed top-24 sm:right-8 right-4 z-40">
        <Link
          to="/accessories/cart"
          className="relative flex items-center justify-center w-14 h-14 bg-indigo-600 text-white rounded-full shadow-lg hover:bg-indigo-700 hover:scale-105 transition-all duration-300 border border-white"
        >
          <Icon icon="solar:cart-large-4-bold-duotone" className="w-6.5 h-6.5" />
          {totalCartQty > 0 && (
            <span className="absolute -top-1 -right-1 bg-rose-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center border-2 border-indigo-600">
              {totalCartQty}
            </span>
          )}
        </Link>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 relative z-10">

        {/* Header Block */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <span className="inline-flex items-center gap-1.5 bg-indigo-50 text-indigo-600 border border-indigo-100 rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-widest mb-4">
            <Icon icon="solar:shop-bold-duotone" className="w-3.5 h-3.5" />
            Accessories Catalog
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-gray-900 tracking-tight mb-4">
            Our Medical <span className="text-gradient">Products</span>
          </h1>
          <p className="text-sm text-gray-500 leading-relaxed max-w-xl mx-auto">
            Browse our clinical quality tools and accessories designed specifically to optimize your vision therapy workouts.
          </p>
        </div>

        {/* Premium Pill Track Category Selector */}
        <div className="flex justify-center mb-16">
          <div className="bg-white/80 backdrop-blur-md border border-gray-200/60 p-1.5 rounded-full inline-flex flex-wrap justify-center gap-1 shadow-sm">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-6 py-2.5 rounded-full text-xs font-bold tracking-wider uppercase transition-all duration-300 ${selectedCategory === cat
                  ? "bg-indigo-600 text-white shadow-md shadow-indigo-600/10"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-50/50"
                  }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Loading / Empty States */}
        {loading ? (
          <div className="flex flex-col items-center justify-center h-72 gap-3">
            <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Fetching products...</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-20 bg-white border border-gray-100 rounded-3xl shadow-sm">
            <Icon icon="solar:box-broken" className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-base text-gray-500 font-medium">No products found in this category.</p>
          </div>
        ) : (
          /* Premium Product Grid */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {filteredProducts.map((product) => {
              const qtyInCart = addedToCart[product._id] || 0;
              const hasStock = product.stock > 0;

              return (
                <div
                  key={product._id}
                  onClick={() => navigate(`/accessories/product-details/${product._id}`)}
                  className="group bg-white border border-gray-100 hover:border-indigo-100 rounded-3xl transition-all duration-300 hover:shadow-xl hover:shadow-indigo-950/[0.03] flex flex-col justify-between overflow-hidden cursor-pointer"
                >
                  {/* Top Image Box */}
                  <div className="relative overflow-hidden aspect-square bg-slate-50/50 flex items-center justify-center p-6 border-b border-gray-100">
                    <img
                      src={product.imageUrl}
                      alt={product.name}
                      className="max-h-44 object-contain group-hover:scale-105 transition-transform duration-500"
                    />

                    {/* Stock badge */}
                    <span className={`absolute top-4 left-4 z-10 px-2.5 py-1.5 rounded-xl text-[9px] font-bold uppercase tracking-wider shadow-sm ${hasStock
                      ? "bg-white text-emerald-600 border border-emerald-100"
                      : "bg-white text-rose-500 border border-rose-100"
                      }`}>
                      {hasStock ? "Available" : "Out of Stock"}
                    </span>
                  </div>

                  {/* Body Content */}
                  <div className="p-6 flex-grow flex flex-col justify-between">
                    <div>
                      {/* Name */}
                      <h3 className="font-heading text-base font-bold text-gray-900 group-hover:text-indigo-600 transition-colors truncate mb-1.5">
                        {product.name}
                      </h3>

                      {/* Description snippet */}
                      <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed mb-6">
                        {product.description?.replace(/<\/?[^>]+(>|$)/g, "") || "Clinical grade quality accessories designed for optimal training support."}
                      </p>
                    </div>

                    {/* Footer Row */}
                    <div className="flex items-center justify-between gap-2 pt-4 border-t border-gray-100/60">
                      {/* Price */}
                      <div className="flex flex-col">
                        <span className="text-xs text-gray-400 font-semibold uppercase tracking-widest scale-90 origin-left">Price</span>
                        <span className="text-lg font-extrabold text-gray-900 font-heading">
                          ৳{product.price}
                        </span>
                      </div>

                      {/* Cart Interactive Trigger - StopPropagation prevents navigating to details */}
                      <div onClick={(e) => e.stopPropagation()}>
                        {qtyInCart > 0 ? (
                          <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-100 p-1 rounded-xl">
                            <button
                              onClick={() => removeFromCart(product._id)}
                              className="w-6.5 h-6.5 rounded-lg bg-white border border-gray-200/50 hover:bg-gray-100 text-gray-600 flex items-center justify-center transition-colors"
                            >
                              <Icon icon="solar:minus-linear" className="w-3 h-3" />
                            </button>
                            <span className="w-6 text-center text-xs font-bold text-gray-800">
                              {qtyInCart}
                            </span>
                            <button
                              onClick={() => addToCart(product)}
                              disabled={!hasStock}
                              className="w-6.5 h-6.5 rounded-lg bg-white border border-gray-200/50 hover:bg-gray-100 text-gray-600 flex items-center justify-center transition-colors disabled:opacity-50"
                            >
                              <Icon icon="solar:add-linear" className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => addToCart(product)}
                            disabled={!hasStock}
                            className="w-10 h-10 rounded-xl bg-indigo-50 hover:bg-indigo-600 text-indigo-600 hover:text-white transition-all flex items-center justify-center cursor-pointer"
                            title="Add to cart"
                          >
                            <Icon icon="solar:cart-plus-bold" className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Product;
